import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostDTO } from '@bb/shared';
import { api } from '@/lib/api';
import { useLiveEvent } from '@/lib/live';

/** How far back an unacknowledged emergency keeps sitting at the top. */
const WINDOW_HOURS = 12;

const EMERGENCY_KEY = ['posts', 'emergencies'] as const;

export type NotificationState = 'unsupported' | 'default' | 'granted' | 'denied';

function isLiveEmergency(post: PostDTO): boolean {
  return post.analysis?.category === 'EMERGENCY' && !post.deletedAt && !post.isRead;
}

function withinWindow(post: PostDTO): boolean {
  return Date.now() - new Date(post.postedAt).getTime() < WINDOW_HOURS * 60 * 60 * 1000;
}

/**
 * Emergencies awaiting acknowledgement.
 *
 * "Acknowledged" is deliberately the existing read flag rather than a new
 * column: marking something read is already the gesture for "I have seen this",
 * and a second, parallel notion of seen would inevitably drift from it.
 */
export function useEmergencyAlerts() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: EMERGENCY_KEY,
    queryFn: () =>
      api.posts.list({
        categories: ['EMERGENCY'],
        isRead: false,
        isDeleted: false,
        sort: 'newest',
        limit: 20,
      }),
    staleTime: 60_000,
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: EMERGENCY_KEY });
  }, [queryClient]);

  // A post almost always arrives before it is classified, so an emergency is
  // usually revealed by the update rather than by the creation.
  useLiveEvent('post.created', (post) => {
    if (isLiveEmergency(post)) refresh();
  });
  useLiveEvent('post.updated', () => refresh());

  // Filtered client-side as well as server-side: acknowledging patches the
  // cached row optimistically rather than refetching, so without this the card
  // would linger until the next round trip.
  const alerts = (query.data?.items ?? []).filter(
    (post) => isLiveEmergency(post) && withinWindow(post),
  );

  return { alerts, isLoading: query.isPending };
}

/**
 * Desktop notifications for incoming emergencies.
 *
 * Mounted once at the shell rather than inside the feed: an urgent post has to
 * reach the operator whichever screen happens to be open, and the browser tab
 * is usually in the background anyway.
 */
export function useEmergencyNotifications() {
  const [permission, setPermission] = useState<NotificationState>(() =>
    typeof Notification === 'undefined'
      ? 'unsupported'
      : (Notification.permission as NotificationState),
  );

  // Track what has already been announced so the `post.updated` echo the
  // classifier emits moments after arrival does not fire a second time.
  const announced = useRef(new Set<string>());

  const notify = useCallback((post: PostDTO) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (announced.current.has(post.id)) return;
    announced.current.add(post.id);

    const notification = new Notification(`URGENT · @${post.author.handle}`, {
      body: post.analysis?.summary ?? post.text.slice(0, 180),
      tag: post.id,
      requireInteraction: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }, []);

  useLiveEvent('post.created', (post) => {
    if (isLiveEmergency(post)) notify(post);
  });
  useLiveEvent('post.updated', (post) => {
    if (isLiveEmergency(post)) notify(post);
  });

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setPermission(Notification.permission as NotificationState);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    setPermission((await Notification.requestPermission()) as NotificationState);
  }, []);

  return { permission, requestPermission };
}
